'use client';

import { BrandName, StyleSchema } from '@models/color-schema.model';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectColors, 
  selectBrandName, 
  selectDisturber,
  selectLogo,
  selectTypography,
  setColors, 
  setBrandSchema,
  setDisturber,
  setTypography, 
  selectHero,
  setHero,
  selectBackgrounds,
  setBackgrounds,
  selectBrandId
} from '@store/features/styleSchemaSlice';
import { BRAND_CONFIGS } from '@config/brandConfigs';

const defaultSchema: StyleSchema = BRAND_CONFIGS.klier;

const StyleSchemaContext = createContext<{
  currentSchema: StyleSchema;
  setSchema: (schema: StyleSchema) => void;
  setActiveBrand: (brandName: BrandName) => void;
  brandNames: BrandName[];
}>({
  currentSchema: defaultSchema,
  setSchema: () => {},
  setActiveBrand: () => {},
  brandNames: ['klier', 'essanelle', 'cosmo', 'hairexpress', 'styleboxx', 'supercut']
});

export const StyleSchemaProvider = ({ children }: { children: React.ReactNode }) => {
  const colors = useSelector(selectColors);
  const brandName = useSelector(selectBrandName);
  const logo = useSelector(selectLogo);
  const disturber = useSelector(selectDisturber);
  const typography = useSelector(selectTypography);
  const hero = useSelector(selectHero);
  const backgrounds = useSelector(selectBackgrounds);
  const brandId = useSelector(selectBrandId);
  const dispatch = useDispatch();

  const currentSchema = useMemo(() => {
    return {
      name: brandName,
      title: brandName.charAt(0).toUpperCase() + brandName.slice(1),
      brandString: brandName,
      colors: colors,
      logo: logo,
      disturber: disturber,
      typography: typography,
      hero: hero,
      backgrounds: backgrounds,
      brandId: brandId
    };
  }, [brandName, colors, logo, disturber, typography, hero, backgrounds, brandId]);

  const setSchema = (schema: StyleSchema) => {
    dispatch(setColors(schema.colors));
    if (schema.typography) {
      dispatch(setTypography(schema.typography));
    }
    if (schema.disturber) {
      dispatch(setDisturber(schema.disturber));
    }
    if (schema.hero) {
      dispatch(setHero(schema.hero));
    }
    if (schema.backgrounds) {
      dispatch(setBackgrounds(schema.backgrounds));
    }
  };
  
  const setActiveBrand = (brandName: BrandName) => {
    dispatch(setBrandSchema(brandName));
  };
  
  const brandNames: BrandName[] = [
    'klier', 'essanelle', 'cosmo', 'hairexpress', 'styleboxx', 'supercut'
  ];

  useEffect(() => {
    // Update CSS variables when schema changes
    document.documentElement.style.setProperty('--primary-color', currentSchema.colors.primary.hex);
    document.documentElement.style.setProperty('--secondary-color', currentSchema.colors.secondary.hex);
    document.documentElement.style.setProperty('--accent-color', currentSchema.colors.accent.hex);
    
    // Set typography variables if available
    if (currentSchema.typography) {
      document.documentElement.style.setProperty('--font-family', currentSchema.typography.fontFamily);
      if (currentSchema.typography.headingsFontFamily) {
        document.documentElement.style.setProperty('--headings-font-family', currentSchema.typography.headingsFontFamily);
      }
      if (currentSchema.typography.primaryFontWeight) {
        document.documentElement.style.setProperty('--primary-font-weight', currentSchema.typography.primaryFontWeight.toString());
      }
    }
    
    // Update favicon if available
    const faviconEl = document.querySelector('link[rel="icon"]');
    if (faviconEl && currentSchema.logo) {
      faviconEl.setAttribute('href', currentSchema.logo.favicon);
    }
  }, [currentSchema]);

  return (
    <StyleSchemaContext.Provider value={{ 
      currentSchema, 
      setSchema, 
      setActiveBrand,
      brandNames
    }}>
      {children}
    </StyleSchemaContext.Provider>
  );
};

export const useStyleSchema = () => useContext(StyleSchemaContext);